import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin, MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {SequenceTopology} from 'constants/SequenceConstants';
import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {AssemblyWizardBanner, LegacyBulkAssemblyBanner} from 'molbio/bulk-assembly/Banners';
import {
  BulkAssemblyMethod,
  BulkAssemblyProcedure,
  CloningBulkAssemblyMethod,
  CloningMethodData,
  DEFAULT_GIBSON_PARAMETERS,
  DEFAULT_GOLDEN_GATE_PARAMETERS,
  DEFAULT_HOMOLOGY_PARAMETERS,
  DisplayFragmentProductionMethod,
  GibsonParameters,
  GoldenGateParameters,
  HomologyParameters,
  METHOD_TO_DISPLAY,
  PreviousTool,
  SEQUENCE_TOPOLOGY_TO_BULK_ASSEMBLY_TOPOLOGY,
  SettingsModalBulkAssemblyParams,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {BinsCount} from 'molbio/bulk-assembly/setup-modal/controls/BinsCount';
import {
  areGibsonParametersValid,
  GibsonParametersControls,
} from 'molbio/bulk-assembly/setup-modal/controls/GibsonParametersControls';
import {
  areGoldenGateParametersValid,
  GoldenGateParametersControls,
} from 'molbio/bulk-assembly/setup-modal/controls/GoldenGateParametersControls';
import {HomologyParametersControls} from 'molbio/bulk-assembly/setup-modal/controls/HomologyParametersControls';
import {createEmptyFragmentBins, makeProcedureBinFields} from 'molbio/bulk-assembly/util/BulkAssemblyUtil';
import {TopologyDropdown} from 'molbio/TopologyDropdown';
import StudySearchInput, {StudySearchItem} from 'studies/StudySearchInput';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import Modal from 'ui-library/modals/Modal';
import ToggleGroup from 'ui-library/ToggleGroup';
import {assertNever} from 'util/Types';

const CLONING_METHOD_TO_DESCRIPTION_TEXT: {
  [method in CloningBulkAssemblyMethod]: {primary: string; secondary: string};
} = {
  [BulkAssemblyMethod.GOLDEN_GATE]: {
    primary:
      'Join up to 15 DNA fragments into a single piece using Type IIS restriction enzymes and T4 DNA ligase.',
    secondary: 'Optionally, you can use primers to introduce cut sites via PCR.',
  },
  [BulkAssemblyMethod.GIBSON]: {
    primary: 'Join up to 15 DNA fragments in a single-tube isothermal reaction.',
    secondary:
      'Gibson assembly uses primers to create homology regions between adjacent fragments. You can fine-tune the primer parameters below.',
  },
  [BulkAssemblyMethod.HOMOLOGY]: {
    primary: 'Join DNA fragments using homology regions.',
    secondary:
      'Benchling will automatically detect complementary overlaps, or homology regions, between adjacent fragments.',
  },
};

export interface CloningSetupModalProps {
  initialMethodData?: CloningMethodData;
  initialFolderId?: string;
  initialName?: string;
  initialNumFragmentBins?: number;
  initialTopology?: SequenceTopology;
  isInitialSetup: boolean;
  canEditAssembly: boolean;
  canEditFolder: boolean;
  canEditName: boolean;
  fragmentProductionMethodOverride?: DisplayFragmentProductionMethod;
  onRequestClose: () => void;
  onRequestSubmit: (newAssemblyParams: SettingsModalBulkAssemblyParams) => void;
  previousTool?: PreviousTool;
  actionOrigin: ActionOrigin;
}

/**
 * A modal to choose or update cloning bulk assembly parameters.
 *
 * @param {CloningSetupModalProps} props Component props
 * @param {CloningMethodData} [props.initialMethodData] The initial cloning method parameters to populate the modal with
 * @param {string} [props.initialFolderId] The initial folder ID to populate the modal with
 * @param {string}  props.initialName The initial assemblyName to populate when editing an existing assembly settings
 * @param {number} props.initialNumFragmentBins The number of fragment bins to initially populate the dropdown with
 * @param {SequenceTopology} props.initialTopology The initial construct topology to populate the modal with
 * @param {boolean} props.isInitialSetup Whether this modal is being shown as part of initial assembly setup or while editing assembly settings after an assembly has been created
 * @param {boolean} props.canEditAssembly Whether the assembly can be edited, which is not permitted after finalization and depends on MolBio feature gating
 * @param {boolean} props.canEditFolder Whether the user can edit the assembly folder, which is permitted after finalization
 * @param {boolean} props.canEditName Whether the user can edit the assembly name, which is permitted after finalization
 * @param {boolean} [props.fragmentProductionMethodOverride] An override for the fragment production method in a Golden Gate assembly.
 * @param {Function} props.onRequestClose Handler for closing the modal without submitting
 * @param {Function} props.onRequestSubmit Handler for submitting the modal
 * @param {PreviousTool} [props.previousTool] Which tool (e.g. Assembly Wizard legacy Bulk Assembly) the modal was launched from
 * @param {ActionOrigin} props.actionOrigin The place from which the modal was launched, for analytics purposes
 * @constructor
 */
export function CloningSetupModal(props: CloningSetupModalProps): JSX.Element {
  const [name, setName] = useState(props.initialName ?? 'Untitled assembly');
  const [folderId, setFolderId] = useState<string | null>(
    props.initialFolderId ?? FolderDefaults.get(FolderDefaultsKey.INVENTORY) ?? null
  );
  const [studySearchItems, setStudySearchItems] = useState<Array<StudySearchItem>>([]);
  const [numFragmentBins, setNumFragmentBins] = useState<number | null>(props.initialNumFragmentBins ?? 2);
  const [isNumFragmentBinsValid, setIsNumFragmentBinsValid] = useState(true);
  const [topology, setTopology] = useState(props.initialTopology ?? SequenceTopology.CIRCULAR);
  const [cloningMethod, setBulkAssemblyMethod] = useState<CloningBulkAssemblyMethod>(
    props.initialMethodData?.type ?? BulkAssemblyMethod.GOLDEN_GATE
  );
  const [goldenGateParameters, setGoldenGateParameters] = useState<GoldenGateParameters>(
    props.initialMethodData?.type === BulkAssemblyMethod.GOLDEN_GATE
      ? props.initialMethodData.parameters
      : DEFAULT_GOLDEN_GATE_PARAMETERS
  );
  const [gibsonParameters, setGibsonParameters] = useState<GibsonParameters>(
    props.initialMethodData?.type === BulkAssemblyMethod.GIBSON
      ? props.initialMethodData.parameters
      : DEFAULT_GIBSON_PARAMETERS
  );
  const [homologyParameters, setHomologyParameters] = useState<HomologyParameters>(
    props.initialMethodData?.type === BulkAssemblyMethod.HOMOLOGY
      ? props.initialMethodData.parameters
      : DEFAULT_HOMOLOGY_PARAMETERS
  );

  useEffect(() => {
    analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.OPENED_BULK_ASSEMBLY_SETUP_MODAL, {
      actionOrigin: props.actionOrigin,
      cloningMethod,
    });
  }, [cloningMethod, props.actionOrigin]);

  function disableSave(): boolean {
    if (name.trim() === '' || folderId == null || !isNumFragmentBinsValid) {
      return true;
    } else if (cloningMethod === BulkAssemblyMethod.GOLDEN_GATE) {
      return !areGoldenGateParametersValid(goldenGateParameters);
    } else if (cloningMethod === BulkAssemblyMethod.GIBSON) {
      return !areGibsonParametersValid(gibsonParameters);
    } else if (cloningMethod === BulkAssemblyMethod.HOMOLOGY) {
      return false;
    } else {
      return true;
    }
  }

  function buildMethodData(): CloningMethodData {
    switch (cloningMethod) {
      case BulkAssemblyMethod.GIBSON: {
        return {
          type: BulkAssemblyMethod.GIBSON,
          parameters: gibsonParameters,
        };
      }
      case BulkAssemblyMethod.GOLDEN_GATE: {
        return {
          type: BulkAssemblyMethod.GOLDEN_GATE,
          parameters: goldenGateParameters,
        };
      }
      case BulkAssemblyMethod.HOMOLOGY: {
        return {
          type: BulkAssemblyMethod.HOMOLOGY,
          parameters: homologyParameters,
        };
      }
      default:
        assert(false, `Unexpected cloning method: ${assertNever(cloningMethod)}`);
    }
  }

  function buildProcedureJson(): BulkAssemblyProcedure {
    const method = buildMethodData();
    return {
      method,
      topology: SEQUENCE_TOPOLOGY_TO_BULK_ASSEMBLY_TOPOLOGY[topology],
      ...makeProcedureBinFields(
        createEmptyFragmentBins(
          numFragmentBins!,
          method.parameters.defaultFragmentProductionMethod,
          topology,
          method.type
        )
      ),
      fragments: [],
      constructs: [],
    };
  }

  return (
    <Modal
      title={props.isInitialSetup ? 'Assemble DNA' : 'Assembly settings'}
      buttons={[
        {
          text: 'Cancel',
          cancel: true,
        },
        {
          text: 'Save',
          submit: true,
          disabled: disableSave(),
          style: ButtonStyle.SECONDARY,
        },
      ]}
      onRequestClose={() => {
        analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.CLOSED_REDESIGNED_BULK_ASSEMBLY_MODAL, {
          actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL,
        });
        props.onRequestClose();
      }}
      onRequestSubmit={() => {
        props.onRequestSubmit({
          name,
          folderId: folderId!,
          procedureJson: buildProcedureJson(),
          studyIds: studySearchItems.map(({id}) => id),
        });
      }}
    >
      {props.isInitialSetup &&
        props.previousTool != null &&
        (props.previousTool === PreviousTool.LEGACY_BULK_ASSEMBLY ? (
          <LegacyBulkAssemblyBanner
            onClick={() => {
              analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.CLOSED_REDESIGNED_BULK_ASSEMBLY_MODAL, {
                actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_BETA_BANNER,
                previousTool: props.previousTool,
              });
              props.onRequestClose();
            }}
          />
        ) : (
          <AssemblyWizardBanner
            onClick={() => {
              analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.CLOSED_REDESIGNED_BULK_ASSEMBLY_MODAL, {
                actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_BETA_BANNER,
                previousTool: props.previousTool,
              });
              props.onRequestClose();
            }}
          />
        ))}
      <TextField
        value={name}
        placeholder="Untitled assembly"
        label="Name"
        labelStyle={LabelStyle.LABEL}
        onBlur={() => {
          analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_NAME_FIELD, {
            actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL,
            cloningMethod,
          });
        }}
        onChange={(e) => {
          setName(e.target.value);
        }}
        isRequired={true}
        dataTestAttrs={{component: 'CloningSetupModal', element: 'nameInput'}}
        disabled={!props.canEditName}
      />
      <div className={css(styles.margin)}>
        <FormGroup>
          <FieldLabel label={DEFAULT_PROJECT_FOLDER_LABEL_TEXT} isRequired={true} />
          <FolderSelector
            enableRedesign={true}
            showInventory={true}
            value={folderId}
            onChange={(e) => {
              setFolderId(e.target.value);
            }}
            unsetOnFetchFail={false}
            readOnly={!props.canEditFolder}
            checkExistingItemForDataModsMovement={!props.isInitialSetup}
          />
        </FormGroup>
      </div>
      {/** There is some complexity in editing Studies, so only show the input on the initial setup. Users can later edit the associated
           Studies from the Metadata tab. */}
      {BenchlingConfig.STUDIES_ENABLE && props.isInitialSetup && (
        <div className={css(styles.margin)}>
          <FormGroup>
            <FieldLabel label="Studies" />
            <StudySearchInput
              onChange={(studySearchItems: Array<StudySearchItem>) => {
                setStudySearchItems(studySearchItems);
              }}
              shouldAllowMultiple={true}
              selectedItems={studySearchItems}
            />
          </FormGroup>
        </div>
      )}
      <div className={css(styles.flex, styles.margin)}>
        <div className={css(styles.column)}>
          <BinsCount
            disabled={!props.isInitialSetup}
            value={numFragmentBins}
            onChangeValue={({isValid, value}) => {
              setNumFragmentBins(value);
              setIsNumFragmentBinsValid(isValid);
            }}
          />
        </div>
        <div className={css(styles.column)}>
          <TopologyDropdown
            label="Topology of construct"
            topology={topology}
            onChange={(sequenceTopology) => {
              setTopology(sequenceTopology);
            }}
            disabled={!props.canEditAssembly}
          />
        </div>
      </div>
      <FormGroup>
        <FieldLabel label="Cloning method" />
        <ToggleGroup
          options={[
            BulkAssemblyMethod.GOLDEN_GATE,
            BulkAssemblyMethod.GIBSON,
            BulkAssemblyMethod.HOMOLOGY,
          ].map((method: CloningBulkAssemblyMethod) => ({
            id: method,
            buttonProps: {text: METHOD_TO_DISPLAY[method]},
            dataTestAttrs: {
              component: 'CloningSetupModal',
              element: 'BulkAssemblyMethodToggle',
              key: method,
            },
          }))}
          selection={cloningMethod}
          onChangeSelection={(method) => {
            analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_CLONING_METHOD_FIELD, {
              actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL,
              cloningMethod,
            });
            setBulkAssemblyMethod(method);
          }}
          size={ButtonSize.SMALL}
          isFullWidth={true}
          disabled={!props.isInitialSetup}
        />
      </FormGroup>
      <InlineBanner
        text={CLONING_METHOD_TO_DESCRIPTION_TEXT[cloningMethod].primary}
        supportingInfo={CLONING_METHOD_TO_DESCRIPTION_TEXT[cloningMethod].secondary}
        action={{actionType: InlineBannerActionType.ACCORDION}}
        bannerType={InlineBannerType.INFO}
        hideIcon={true}
      />
      {
        cloningMethod !== BulkAssemblyMethod.HOMOLOGY && <hr /> // only show HR if there are parameters following
      }
      {cloningMethod === BulkAssemblyMethod.GOLDEN_GATE && (
        <GoldenGateParametersControls
          goldenGateParameters={goldenGateParameters}
          patchGoldenGateParameters={(patch: Partial<GoldenGateParameters>) => {
            setGoldenGateParameters({...goldenGateParameters, ...patch});
          }}
          fragmentProductionMethodOverride={props.fragmentProductionMethodOverride}
          isReadOnly={!props.canEditAssembly}
        />
      )}
      {cloningMethod === BulkAssemblyMethod.GIBSON && (
        <GibsonParametersControls
          gibsonParameters={gibsonParameters}
          patchGibsonParameters={(patch: Partial<GibsonParameters>) => {
            setGibsonParameters({...gibsonParameters, ...patch});
          }}
          fragmentProductionMethodOverride={props.fragmentProductionMethodOverride}
          isReadOnly={!props.canEditAssembly}
        />
      )}
      {cloningMethod === BulkAssemblyMethod.HOMOLOGY && (
        <HomologyParametersControls
          homologyParameters={homologyParameters}
          patchHomologyParameters={(patch: Partial<HomologyParameters>) => {
            setHomologyParameters({...homologyParameters, ...patch});
          }}
          isReadOnly={!props.canEditAssembly}
        />
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  flex: {
    display: 'flex',
    columnGap: '22px',
  },
  column: {
    flex: 1,
  },
  margin: {
    marginTop: '16px',
  },
});
